import ArticleCallToAction from '@/cmsComponents/ArticleCallToAction';
import { MarkdownContent } from '@/cmsComponents/MarkdownContent';
import { IArticleCta } from '@/types/cms';
import classNames from 'classnames';
import React from 'react';

import styles from './ArticleContentWithCta.module.scss';

type CtaPosition = 'TOP' | 'MIDDLE' | 'BOTTOM';
type ArticleContentWithCtaProps = {
  content: string;
  articleCta?: IArticleCta;
  ctaPosition: CtaPosition;
};

export const ARTICLE_CTA_INSERT = '{{INSERT_CTA_HERE}}';
const ARTICLE_CTA_INSERT_REGEX = new RegExp(/^\s*\{\{INSERT_CTA_HERE}}\s*$/gm);

export function articleContentHasCtaInsert(content: string) {
  return ARTICLE_CTA_INSERT_REGEX.test(content);
}

const generateOldCta = (cta: IArticleCta, ctaPosition: CtaPosition) => {
  let ctaClass = '';
  if (ctaPosition === 'BOTTOM') {
    ctaClass = classNames(styles.ctaBannerBottom);
  }
  return <ArticleCallToAction {...cta} ctaPosition={ctaPosition} className={ctaClass} />;
};

export function addCtaToBlocks(
  blocks: React.ReactNode[],
  props: Pick<ArticleContentWithCtaProps, 'articleCta' | 'ctaPosition'>,
  ctaPosition: CtaPosition,
  hasCtaInsert: boolean,
) {
  if ('articleCta' in props && props.articleCta) {
    // If there was no INSERT_CTA_HERE token but the CTA is supposed to be put in the middle,
    // we should force the CTA to be put at the end instead
    const endPositionOverride =
      props.ctaPosition === 'MIDDLE' && ctaPosition === 'BOTTOM' && !hasCtaInsert;
    if (props.ctaPosition === ctaPosition || endPositionOverride) {
      blocks.push(generateOldCta(props.articleCta, props.ctaPosition));
    }
  }
}

const ArticleContentWithCta: React.FC<ArticleContentWithCtaProps> = props => {
  const { content } = props;

  const splitItems = content.split(ARTICLE_CTA_INSERT_REGEX);
  const hasCtaInsert = splitItems.length > 1;

  const blocks: React.ReactNode[] = [];

  addCtaToBlocks(blocks, props, 'TOP', hasCtaInsert);

  for (let i = 0; i < splitItems.length; i++) {
    blocks.push(<MarkdownContent source={splitItems[i]} />);

    // Don't add a CTA after the last block
    if (i + 1 < splitItems.length) {
      addCtaToBlocks(blocks, props, 'MIDDLE', hasCtaInsert);
    }
  }

  addCtaToBlocks(blocks, props, 'BOTTOM', hasCtaInsert);

  return <>{blocks}</>;
};

export default ArticleContentWithCta;
